import React, { useMemo } from "react";
import { Paper, Grid, Typography, Button, Chip, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

function isTrialExpired(tenant) {
  if (tenant.account_type !== "test") return false;
  if (!tenant.trial_expires_at) return false;
  const exp = new Date(tenant.trial_expires_at);
  return Number.isFinite(exp.getTime()) && exp <= new Date();
}

const fmtDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleString();
};

export default function TenantRow({ tenant, onOpen }) {
  const { t } = useTranslation();

  const expired = useMemo(() => isTrialExpired(tenant), [tenant]);

  const createdAt = useMemo(() => fmtDateTime(tenant.created_at), [tenant.created_at]);
  const firstLoginAt = useMemo(() => fmtDateTime(tenant.first_login_at), [tenant.first_login_at]);
  const trialExpiresAt = useMemo(() => fmtDateTime(tenant.trial_expires_at), [tenant.trial_expires_at]);

  const adminName = useMemo(() => {
    const fn = (tenant.contact_admin_first_name || "").trim();
    const ln = (tenant.contact_admin_last_name || "").trim();
    const name = `${fn} ${ln}`.trim();
    return name || null;
  }, [tenant]);

  const adminEmail = (tenant.contact_admin_email || "").trim() || null;

  const gatewayOk = !!(tenant.gateway_sync_code && String(tenant.gateway_sync_code).trim());

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} sm={7}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 700 }}>{tenant.name}</Typography>

            <Chip
              size="small"
              label={
                tenant.account_type === "test"
                  ? t("tenantRow.type.test", "Test")
                  : t("tenantRow.type.production", "Produkcia")
              }
              variant="outlined"
            />

            {!tenant.is_active ? (
              <Chip size="small" color="error" label={t("tenantRow.status.inactive", "Neaktívny")} />
            ) : null}

            {expired ? (
              <Chip
                size="small"
                color="warning"
                label={t("tenantRow.status.trialExpired", "Skúšobná doba vypršala")}
              />
            ) : null}

            <Chip
              size="small"
              variant="outlined"
              color={gatewayOk ? "success" : "default"}
              label={
                gatewayOk
                  ? t("tenantRow.gateway.ok", "Gateway OK")
                  : t("tenantRow.gateway.missing", "Gateway chýba")
              }
            />

            {tenant.contract_license ? (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${t("tenantRow.program", "Program")}: ${tenant.contract_license}`}
              />
            ) : null}
          </Stack>

          {/* line 1: ids */}
          <Typography variant="body2" color="text.secondary">
            {t("tenantRow.slug", "Slug")}: <b>{tenant.slug}</b> • {t("tenantRow.id", "ID")}: {tenant.id}
            {tenant.ico ? (
              <>
                {" "}
                • {t("tenantRow.ico", "IČO")}: <b>{tenant.ico}</b>
              </>
            ) : null}
          </Typography>

          {/* line 2: lifecycle + contact */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {createdAt ? (
              <>
                {t("tenantRow.createdAt", "Vytvorený")}: <b>{createdAt}</b>
              </>
            ) : null}

            {firstLoginAt ? (
              <>
                {" "}
                • {t("tenantRow.firstLoginAt", "Prvé prihlásenie")}: <b>{firstLoginAt}</b>
              </>
            ) : (
              <>
                {" "}
                • {t("tenantRow.firstLoginAt", "Prvé prihlásenie")}:{" "}
                <b>{t("tenantRow.never", "nikdy")}</b>
              </>
            )}

            {tenant.account_type === "test" ? (
              <>
                {" "}
                • {t("tenantRow.trialExpiresAt", "Trial do")}: <b>{trialExpiresAt || "—"}</b>
              </>
            ) : null}

            {adminName || adminEmail ? (
              <>
                {" "}
                • {t("tenantRow.adminContact", "Admin")}:{" "}
                <b>
                  {adminName || "—"}
                  {adminEmail ? ` (${adminEmail})` : ""}
                </b>
              </>
            ) : null}
          </Typography>

          {/* line 3: využívanie — počet anonymizácií dokumentov */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("tenantRow.anonymizedCount", "Anonymizovaných dokumentov")}:{" "}
            <b>{Number.isFinite(Number(tenant.anonymized_count)) ? Number(tenant.anonymized_count) : 0}</b>
          </Typography>

          {/* optional: deactivation reason */}
          {!tenant.is_active && tenant.deactivation_reason ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("tenantRow.deactivationReason", "Dôvod deaktivácie")}:{" "}
              <b>{tenant.deactivation_reason}</b>
            </Typography>
          ) : null}
        </Grid>

        <Grid item xs={12} sm={5} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={() => onOpen(tenant.id)}>
            {t("tenantRow.actions.detail", "Detail")}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
}